/**
 * Curricula list — admin page. Rendering + filters come from the shared
 * `CurriculaListView`; this wrapper adds the admin-only "create curriculum"
 * modal and its mutation. See docs/FeatureDesigns/teacher-curriculum-view.md.
 */
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useLocation } from 'react-router-dom';
import { adminApi } from '../api/adminApi';
import type { Curriculum, CurriculumListItem } from '../types';
import { GRADE_OPTIONS, ALL_SUBJECT_OPTIONS } from '@padcanvas/shared';
// The FULL programme list, not the base five: this is an authoring surface, and
// an AP curriculum cannot be created without being able to name AP.
import { ALL_PROGRAMME_FORM_OPTIONS } from '@shared-ui/reference-data/examOptions';
import { Modal, Input, Select, Button } from '@shared-ui/components';
import { CurriculaListView } from '@shared-ui/curriculum-view';
import { IS_PROD_ENV } from '../api/auth';

const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

export function CurriculaList() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const [curModal, setCurModal] = useState<Partial<Curriculum> | null>(null);

  // The list keeps its filters in the query string, so the way back has to
  // carry it — the detail page's "← Curricula" link is a plain path and would
  // otherwise drop every filter. Browser Back already works; this is for the link.
  //
  // Built from the CURRENT location rather than a hardcoded `/curriculum`: the
  // list is mounted as a tab of Modules & Curriculum, and naming the retired
  // path sent the user through a redirect that dropped the very filters this
  // is here to preserve.
  const openCurriculum = (id: string) =>
    navigate(`/curriculum/${id}`, { state: { backTo: `${location.pathname}${location.search}` } });

  const { data, isLoading, error, refetch } = useQuery({ queryKey: ['curricula'], queryFn: adminApi.listCurricula });
  const curricula = (data?.curricula ?? []) as CurriculumListItem[];

  const saveCurriculum = useMutation({
    mutationFn: (c: Partial<Curriculum>) => adminApi.saveCurriculum(c),
    onSuccess: (c) => { qc.invalidateQueries({ queryKey: ['curricula'] }); setCurModal(null); openCurriculum(c.id); },
  });

  return (
    <CurriculaListView
      items={curricula}
      loading={isLoading}
      error={error}
      onRetry={() => refetch()}
      onOpen={openCurriculum}
      // Prod curricula come from the dev→prod sync, so authoring one here — for
      // ANY admin, tenant or platform — makes a syllabus the sync did not make
      // and will overwrite or orphan. The shared view hides the button when
      // `onNew` is absent.
      onNew={IS_PROD_ENV ? undefined : () => setCurModal({ examTrack: false, isActive: true })}
      showOwnership
      infoText="Each curriculum is one syllabus — a board/exam + subject + grade (e.g. “CBSE Class 8 Maths”). Open a row to author its placements over the canonical taxonomy."
    >
      {/* Create curriculum modal (admin only) */}
      <Modal isOpen={!!curModal} onClose={() => setCurModal(null)} title="New Curriculum" size="sm">
        {curModal && (
          <div style={{ display: 'grid', gap: 12 }}>
            <label>Id (stable key, e.g. <code>cbse-8-maths</code>)
              <Input value={curModal.id ?? ''} onChange={(e) => setCurModal({ ...curModal, id: e.target.value })} placeholder="cbse-8-maths" />
            </label>
            <label>Name
              <Input value={curModal.name ?? ''} onChange={(e) => setCurModal({ ...curModal, name: e.target.value })} placeholder="CBSE Class 8 Maths" />
            </label>
            <label>Board / exam track
              <Select value={curModal.board ?? ''} onChange={(e) => setCurModal({ ...curModal, board: (e.target.value || undefined) as Curriculum['board'] })}>
                <option value="">— select —</option>
                {ALL_PROGRAMME_FORM_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </Select>
            </label>
            <label>Subject
              <Select value={curModal.subject ?? ''} onChange={(e) => setCurModal({ ...curModal, subject: (e.target.value || undefined) as Curriculum['subject'] })}>
                <option value="">— select —</option>
                {ALL_SUBJECT_OPTIONS.map(s => <option key={s} value={s}>{cap(s)}</option>)}
              </Select>
            </label>
            <label>Grade (optional — school-board class; omit for exam tracks)
              <Select value={curModal.grade ?? ''} onChange={(e) => setCurModal({ ...curModal, grade: (e.target.value || undefined) as Curriculum['grade'] })}>
                <option value="">— none (exam track) —</option>
                {GRADE_OPTIONS.map(c => <option key={c} value={c}>Class {c}</option>)}
              </Select>
            </label>
            <label>Pool key {curModal.grade || curModal.examTrack
              ? <span style={{ fontWeight: 400 }}>(optional — derived from board + grade)</span>
              : <span style={{ fontWeight: 400 }}>— required: with no grade, every course of this programme would share one pool</span>}
              <Input
                value={curModal.poolKey ?? ''}
                onChange={(e) => setCurModal({ ...curModal, poolKey: e.target.value.trim() || undefined })}
                placeholder={curModal.grade || curModal.examTrack ? 'leave blank to derive' : 'ap:calculus_ab'}
              />
            </label>
            <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input type="checkbox" checked={!!curModal.examTrack} onChange={(e) => setCurModal({ ...curModal, examTrack: e.target.checked })} />
              Exam track (JEE/NEET) rather than a school board
            </label>
            <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input type="checkbox" checked={curModal.isActive ?? true} onChange={(e) => setCurModal({ ...curModal, isActive: e.target.checked })} />
              Active (uncheck to create as inactive)
            </label>
            {saveCurriculum.error ? <div style={{ color: 'var(--color-error)', fontSize: 13 }}>{(saveCurriculum.error as Error).message}</div> : null}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <Button variant="secondary" size="sm" onClick={() => setCurModal(null)}>Cancel</Button>
              <Button variant="primary" size="sm"
                // The server refuses a gradeless non-track curriculum with no pool
                // key; say so before the round trip rather than after it.
                disabled={!curModal.id || !curModal.name || !curModal.board || !curModal.subject
                  || (!curModal.grade && !curModal.examTrack && !curModal.poolKey)
                  || saveCurriculum.isPending}
                onClick={() => saveCurriculum.mutate(curModal)}>Create</Button>
            </div>
          </div>
        )}
      </Modal>
    </CurriculaListView>
  );
}
